/// <reference types="jquery"/>
/// <reference types="bootstrap"/>

import 'bootstrap';
import { store } from '@qmd/store';
import Uppy, { StrictTypes, UppyFile } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3Multipart from '@uppy/aws-s3-multipart';

import Axios from 'axios';
import axiosRetry from 'axios-retry';
import { fromEvent, merge } from 'rxjs';

import { UploadErrors } from '@qmd/components/UploadErrors';

import './custom.scss';
import './static/css/style.css';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import * as jquery from "jquery";

axiosRetry(Axios, {
    retries: 2,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: () => true,
});

type WakeLockSentinal = {
    release(): Promise<void>;
}

declare global {
    interface Window {
        uploadData: Function;
        uppy: any;
        current_path: string;
    }
    interface Navigator {
        wakeLock: {
            request(t: 'screen'): Promise<WakeLockSentinal>;
        }
    }
}

const handlemark = Symbol('error has been handled');
const markHandled = (error: any) => { error.handlemark = handlemark; return error; };
const isErrorHandled = (error: any) => error.handlemark === handlemark;

const uppy = Uppy<StrictTypes>({ allowMultipleUploads: false });
let s3_url_prefix: string = 'uninitialized';

let uppyNewUpload = true;
let wakeLock: WakeLockSentinal | null = null;
let uploadInProgress = false;
// Flag to specify the uid to copy a study from
// or null to specify that we're doing a new upload
let copyStudyUid:string|null = null;


function showErrorModal(title: string, msg: string)
{
    $("#error-modal-title").text(title);
    $("#error-modal-body").text(msg);
    $('#error-modal').modal('show');
}

//When a modal is closed it automatically removes the modal-open class from the body; however, if we had two modals open
//this causes issues. This checks whenever a modal is closed to see if there is another modal still open and if so it
//adds back the modal-open class to the body.
$(document).on('hidden.bs.modal', '.modal', function () {
    $('.modal:visible').length && $(document.body).addClass('modal-open');
});

function showWarnUploadModal(msg: string) {
    $("#warn-upload-modal-body").text(msg);
    $('#warn-upload-modal').modal('show');
}

function buildWarnUploadModalInnerHtml(msgs: String[]) {
    const $warningBody = $("#warn-upload-modal-body");
    $warningBody.empty()
    const $warningListElt = $("<ul></ul>")
    for (const msg of msgs) {
        const $msgElt = $('<li></li>')
        $msgElt.text(msg as string)
        $($warningListElt).append($msgElt)
    }
    $warningBody.append($warningListElt)
    $('#warn-upload-modal').modal('show')
}

const uploadErrors = new UploadErrors();


const onUploadError = (error: Error, message: string) => {
    if ((error as any).response.data && (error as any).response.data.message)
    {
        showErrorModal("Upload Error", 
                       `Got error ${(error as any).status} while attempting to submit upload: ${(error as any).response.data.message}`); 
    }
    if ((error as any).status === 401) {
        console.log('redirect!');
        return;
    }
    uploadErrors.addError(message, error);
}

uppy.use(Dashboard,
    {
        inline: true,
        target: "#uppy",
        hideUploadButton: true,
        disableThumbnailGenerator: true,
        fileManagerSelectionType: 'both',
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        theme: 'dark',
    });

uppy.on('complete', async () => {
    submitUploadData()

    // this will cancel pending uploads & remove all files
    // we dont want to cancel uploads - but since this is on the complete callback
    // it should be safe to call
    uppy.cancelAll();
    uppyNewUpload = true;



});
uppy.on('cancel-all', () => {
    console.log('cancel all!');
    uploadInProgress = false;
})

uppy.on('error', (error) => {
    uppyNewUpload = false;

    if (isErrorHandled(error)) { return; }

    onUploadError(error, 'generic uppy error');
});

uppy.on('upload-error', (file, error, response) => {
    postUpload();

    if (isErrorHandled(error)) { return; }

    onUploadError(error, `upload-error event: ${file}, ${response}`);
});

uppy.on('file-added', () => {
    uploadErrors.clearErrors();
    uppyNewUpload = true;
});
uppy.on('file-removed', () => {
    uploadErrors.clearErrors();
    uppyNewUpload = true
});

const getS3Key = (file: UppyFile) => `${s3_url_prefix}/${file.id}/${file.name}`

const getMyUrl = () => document.location.origin;

const QMDUploader = {
    limit: 10,
    async createMultipartUpload(file: UppyFile): Promise<any> {
        try {
            const resp = (await Axios.post(`${getMyUrl()}/api/s3/create_multipart_upload`, {
                key: getS3Key(file),
                metadata: JSON.stringify(file.meta, (k, v) => v === null ? undefined : v),
                content_type: file.type,
            }));

            return resp.data;
        } catch (e) {
            onUploadError(e, 'create_multipart_upload');
            throw markHandled(e);
        }
    },
    async listParts(file: UppyFile, { uploadId: upload_id, key }: any): Promise<any> {
        try {
            return (await Axios.post(`${getMyUrl()}/api/s3/list_parts`, {
                key,
                upload_id,
            })).data;
        } catch (e) {
            onUploadError(e, 'list_parts');
            throw markHandled(e);
        }
    },
    async prepareUploadPart(file: UppyFile, { uploadId: upload_id, key, body, number: part_number }: any): Promise<any> {
        try {
            return (await Axios.post(`${getMyUrl()}/api/s3/prepare_upload_part`, {
                upload_id,
                part_number,
                key,
            })).data;
        } catch (e) {
            onUploadError(e, 'prepare_upload_part');
            throw markHandled(e);
        }
    },
    async completeMultipartUpload(file: UppyFile, { uploadId: upload_id, key, parts }: any): Promise<any> {
        try {
            return (await Axios.post(`${getMyUrl()}/api/s3/complete_multipart_upload`, {
                upload_id,
                key,
                parts,
            })).data;
        } catch (e) {
            onUploadError(e, 'complete_multipart_upload');
            throw markHandled(e);
        }
    },
    async abortMultipartUpload(file: UppyFile, { uploadId: upload_id, key }: any): Promise<void> {
        // https://github.com/transloadit/uppy/issues/2486
        if (!key || !upload_id) { return; }

        try {
            await Axios.post(`${getMyUrl()}/api/s3/abort_multipart_upload`, {
                key,
                upload_id,
            });
        } catch (e) {
            // this error is a bit different from the others...
            onUploadError(e, 'abort_multipart_upload');
            throw markHandled(e);
        }
    },
}

uppy.use(
    AwsS3Multipart,
    QMDUploader,
);


const postUpload = () => {
    wakeLock?.release();
    wakeLock = null;
    uploadInProgress = false;
}

function warnUserRegardingSettingsIfNecessary(): boolean {

    let serialNum: string = $("#serial-number").val() as string;
    const lc_profile = (($('#lc-profile-select')[0] as HTMLInputElement).value);
    const temp_override = (($('#temperature-override')[0] as HTMLInputElement).value);
    const user_spec_temp = lc_profile == "User Specified"
    const temp_ok = lc_profile == "User Specified" ? (!!temp_override.trim() && !isNaN(+temp_override)) : true;
    const tempInBand: boolean = lc_profile == "User Specified" && temp_ok && 0 <= (+temp_override) && (+temp_override) <= 30;
    const warningBody = generateWarningBody(!serialNum, user_spec_temp && (!temp_ok || !tempInBand), !isSerialNumInDb(serialNum), serialNum)
    const warningState: boolean = !!(warningBody.length);

    if (warningState) {
        buildWarnUploadModalInnerHtml(warningBody)
        return true // warn user
    }
    return  false  // don't warn user
}

window['uploadData'] = async (ignore_all_warnings = false) => {
    if (uploadInProgress) {
        return;
    }

    // if ignore serial num warning, do not display warning (presumes override) .  Otherwise, warn if necessary
    const warningDisplayed = ignore_all_warnings ? false : warnUserRegardingSettingsIfNecessary();

    if (!warningDisplayed) {
        uploadInProgress = true;
        $('#warn-upload-modal').modal('hide');
        $('#upload-button').prop('disabled', true);
        uploadErrors.clearErrors();

        if(copyStudyUid === null) {
            doNewUpload();
        } else {
            submitUploadData();
        }
    }
}

async function doNewUpload() {        
    try {
        s3_url_prefix = (await Axios.post(`${getMyUrl()}/api/s3/get_upload_prefix`)).data;
    } catch (e) {
        onUploadError(e, 'get_upload_prefix');
        return;
    }

    if ('wakeLock' in navigator) {
        if (!!wakeLock) {
            console.warn('wakelock was already taken');
        } else {
            try {
                wakeLock = await navigator.wakeLock.request('screen');
            } catch (e) {
                console.warn('error taking wakeLock', e);
            }
        }
    } else {
        console.warn('wakeLock not supported on this platform');
    }

    // uppy errors will be caught by its own set of handlers
    if (uppyNewUpload) {
        uppy.upload();
    } else {
        uppy.retryAll();
    }
}

async function submitUploadData() {
    try {
        let params:any = {
            study_package: ($('#punchcard-select')[0] as HTMLInputElement).value,
            phantom_type: ($('#phantom-select')[0] as HTMLInputElement).value,
            description: ($('#upload_description')[0] as HTMLInputElement).value,
            lc_profile: ($('#lc-profile-select')[0] as HTMLInputElement).value,
            serial_number: ($('#serial-number')[0] as HTMLInputElement).value,
            temperature_override: ($('#temperature-override')[0] as HTMLInputElement).value,
            protocol:($('#protocol-select')[0] as HTMLInputElement).value,
            share_group_ids: ($('#share-groups-select').val()),
            path: window.current_path,
        }
        if (copyStudyUid !== null) {
            params.copy_from_uid = copyStudyUid;
        } else {
            params.s3_url_prefix = s3_url_prefix;
        }
        let x = await Axios.post(`${getMyUrl()}/api/s3/post_upload`, params);
        location.reload();
    } catch (e) {
        console.log('post response error', e, e.response, e.response.data)
        onUploadError(e, 'post_upload');
    }
    postUpload();
    $('#modalUpload').modal('hide')
}


function generateWarningBody(serialNumWarning: boolean, tempWarning: boolean, serialNumNotInDb: boolean, serialNum: string): String[] {
    let warnings: String[] = [];
    if (serialNumWarning) {
        warnings.push("No serial number provided. Without a serial number you will not have access to " +
            "any reference data for this study nor will you be able to link this to other studies on this phantom.");
    }
    if (tempWarning) {
        warnings.push("Temperature is normally between 0 and 30, please correct temperature before " +
            "submitting if you did not intend to be outside of this range.");
    }
    if (!serialNumWarning && serialNumNotInDb) {
        warnings.push(`We can't find a phantom with the serial number: ${serialNum} in the database.`+
            " Please verify that the serial number is correct.  If you continue with the submission then reference data"+
            " will not be available for this study.");
    }
    return warnings;
}
function hasValidSerialNumber() {
    if (!$('#upload-button').prop('disabled')) {
        let serial_number_val = $("#serial-number").val() as string;
        let phantom_type = $("#phantom-select").val()?.toString();
        if (!serial_number_val) {
            showWarnUploadModal("No serial number provided. Without a serial number you will not have access to " +
                "any reference data for this study nor will you be able to link this to other studies on this phantom.")
            return false;
        } else if (phantom_type && !serial_number_val.toString().startsWith(phantom_type)) {
            showErrorModal("Invalid Serial Number", "The serial number must correspond with the selected phantom type.  The first three digits of the serial number must match the phantom type number.")
            return false;
        } else if (!isSerialNumInDb(serial_number_val)) {
            showWarnUploadModal(`We can't find the a phantom with the serial number: ${serial_number_val} in the database.`+
            " Please verify that the serial number is correct.  If you continue with the submission then reference data"+
            " will not be available for this study");
            return false;
        } else {
            return true;
        }
    }
}

function isSerialNumInDb(serial_number: string) {
    var is_in_db = false;
    $.ajax({url: '/api/vial/existence/' + serial_number,
            contentType: "application/json",
            type: 'GET',
            async: false,
            success: function(data) {
                console.log("data=" + data.exists)
                is_in_db = data.exists;
            },
            error: function(jqXHR, txtStatus, errorThrown) {
                console.log("textStatus=" + txtStatus);
                console.log("errorThrown=" + errorThrown);
            }
    });
    console.log(is_in_db)
    return is_in_db;
}

let checkUploadModalCanUpload = () => {
    const phantom = (($('#phantom-select')[0] as HTMLInputElement).value);
    const lc_profile = (($('#lc-profile-select')[0] as HTMLInputElement).value);
    const protocol = (($('#protocol-select')[0] as HTMLInputElement).value);
    const temp_override = (($('#temperature-override')[0] as HTMLInputElement).value);
    const sn = (($('#serial-number')[0] as HTMLInputElement).value).split("-").pop() || '';
    const temp_ok = (lc_profile == "User Specified Temperature" ? (!!temp_override.trim() && !isNaN(+temp_override)) : true);
    const sn_ok = (sn == "" || !isNaN(+sn));
    const canUpload = ( (uppy.getFiles().length > 0 || copyStudyUid !== null) &&
                        !!phantom && !!protocol && 
                        !!lc_profile && temp_ok && sn_ok);
    ($('#upload-button')[0] as HTMLInputElement).disabled = !canUpload;
};
merge(
    fromEvent(uppy as any as Element, 'file-added'),
    fromEvent(uppy as any as Element, 'file-removed'),
    fromEvent(document.querySelector('#phantom-select') as Element, 'change'),
    fromEvent(document.querySelector('#lc-profile-select') as Element, 'change'),
    fromEvent(document.querySelector('#temperature-override') as Element, 'input'),
    fromEvent(document.querySelector('#serial-number') as Element, 'input'),
    fromEvent(document.querySelector('#protocol-select') as Element, 'change'),
    fromEvent(document.querySelector('#punchcard-select') as Element, 'change'),
    fromEvent(document.querySelector('#upload-data-btn') as Element, 'click')
).subscribe(checkUploadModalCanUpload)

function propagateTemperatureOverride() {
    const lc_profile = (($('#lc-profile-select')[0] as HTMLInputElement).value);
    if (lc_profile == "User Specified") {
        $("#temperature-override").show();
    } else {
        $('#temperature-override').hide();
    }
}

$("#lc-profile-select").on("change", function (event: any) {
    propagateTemperatureOverride();
});


function trySelectFirstProtocol() {
    try {
        // @ts-ignore
        const firstVal = $('#protocol-select').find('option')[0].text
        let index: number;
        if (firstVal.includes('Select Imaging Protocol')) {
            index = 1;
        } else {
            index = 0;
        }
        // change user input to first in dropdown
        // @ts-ignore
        ($('#protocol-select')[0] as HTMLInputElement).value = $('#protocol-select').find('option')[index].value as string;
    } catch (e) {
        // page not loaded yet
    }
}

function dropdownSelectChange(isProtocolSelectChange: boolean) {
    const phantom: string = (($('#phantom-select')[0] as HTMLInputElement).value).split(":")[0];
    const protocol: string = (($('#protocol-select')[0] as HTMLInputElement).value);

    // update serial numbers
    $("#serial-number").attr("list", phantom + "-serial-numbers");
    $("#serial-number").attr("name", phantom + "-serial-number");
    
    function selectFirstThermometerConfigOption() {
        // @ts-ignore
        ($('#lc-profile-select')[0] as HTMLInputElement).value = $('#lc-profile-select').find('option')[0].value as string;
    }

    function hasThermometerConfigs() {
        return $('#lc-profile-select').find('option').length > 0;
    }

    function trySelectFirstThermometerConfigOption() {
        if (hasThermometerConfigs()) {
            selectFirstThermometerConfigOption();
        }
    }


    const allowed: {[key: string]: string[] } = {
        '128_cmri': ['lc', 'ice', 'user'],
        '128_qiba': ['ice', 'user'],
        'dro_qiba': [],
        'else': ['lc', 'user'],
    }

    $('#lc-profile-select').empty()

    const valid = protocol in allowed ? allowed[protocol] : allowed['else'];
    if (valid.length == 0) {
        $('#lc-profile-select-container').hide()
        $('#temperature-override-container').hide()
    } else {
        $('#lc-profile-select-container').show()
        $('#temperature-override-container').show()

        const options = []
        for (const option of valid) {
            switch (option) {
                case 'lc':
                    options.push("<option>Use LC Thermometer</option>")
                    break;
                case 'ice':
                    options.push("<option>Ice Water</option>")
                    break;
                case 'user':
                    options.push("<option>User Specified</option>")
                    break;
            }
        }

        for (const option of options) {
            $('#lc-profile-select').append(option)
        }
    }

    // update protocol dropdown
    if (!isProtocolSelectChange) {
        $("#protocol-select").empty();
        const proto_dict: any = getProtosForPhantom(phantom);
        let include_protos = null;
        if (proto_dict) {
            include_protos = proto_dict.public_protocols;
            const custom_protocols = $("#punchcard-select").find("option:selected").data('customProtocols'); // includes protocols for all phantoms
            const custom_protocol_list = ("" + custom_protocols).split(",");

            if (custom_protocol_list.length > 0) {
                for (const proto in custom_protocol_list) {
                    for (const private_proto in proto_dict.private_protocols) {
                        if (custom_protocol_list[proto] == proto_dict.private_protocols[private_proto][0]) {
                            include_protos.unshift(proto_dict.private_protocols[private_proto]);
                        }
                    }
                }
            }
        }

        if (include_protos) {
            if (include_protos.length > 1) {
                $("#protocol-select").append($("#protocol-select-shadow").find("option:disabled").clone());
            }
            if (include_protos.length == 1) {
                $("#protocol-select").append(`<option selected value="${include_protos[0][0]}">${include_protos[0][1]}</option>`);
            } else {
                for (const proto in include_protos) {
                    $("#protocol-select").append(`<option value="${include_protos[proto][0]}">${include_protos[proto][1]}</option>`);
                }
            }
        } else {
            $("#protocol-select").append($("#protocol-select-shadow").find(`option`).clone());
        }

        // select first protocol subroutine
        trySelectFirstProtocol();
        // propagate protocol change (important if protocol affects thermometer)
        dropdownSelectChange(true);
        trySelectFirstThermometerConfigOption();
        propagateTemperatureOverride();
    }
    checkUploadModalCanUpload()
}


function phantomSelectChange() {
    dropdownSelectChange(false);
}

function protocolSelectChange() {
    dropdownSelectChange(true);
}

$("#phantom-select").on('change', function (event: any) {
    phantomSelectChange();
});
$('#protocol-select').on('change', function (event: any) {
    protocolSelectChange();
});

$("#upload_description").on("keydown", function (event: any) {
    if (event.keyCode === 13) {  // Handle ENTER key
        event.preventDefault();
        if (!$('#upload-button').prop('disabled')) {
            window.uploadData();
        }
    }
});

function getProtosForPhantom(phantom_type: string) {
    if (phantom_type == "") {return null}
    var proto_data;
    $.ajax({url: '/api/phantom_protocols/' + phantom_type,
            contentType: "application/json",
            type: 'GET',
            async: false,
            success: function(data) {
                proto_data = data;
            },
            error: function(jqXHR, txtStatus, errorThrown) {
                console.log("textStatus=" + txtStatus);
                console.log("errorThrown=" + errorThrown);
            }
    });
    return proto_data;
}

function getShareGroups() {
    var share_group_data;
    $.ajax({url: '/api/share_group/',
        contentType: "application/json",
            type: 'GET',
            async: false,
            success: function(data) {
                share_group_data = data;
            },
            error: function(jqXHR, txtStatus, errorThrown) {
                console.log("textStatus=" + txtStatus);
                console.log("errorThrown=" + errorThrown);
            }
    })
    return share_group_data

}


function loadConfigureShareGroups() {
    const $selectElement = $('#share-groups-select');
    const $selectEnable = $('#share-groups-select-enable');
    const $makeShareGroupDefaultButton = $('#share-groups-select-make-default');

    const useShareGroupDefault = localStorage.getItem('useShareGroupDefault');
    if (useShareGroupDefault == "true") {
        let item = localStorage.getItem('shareGroupDefaults');
        const shareGroupDefaults = item ? JSON.parse(item) : null;
        if (shareGroupDefaults) {
            $selectElement.val(shareGroupDefaults)
        }
    }

    $selectEnable.on('click', (e) => {
        e.stopPropagation()
        // @ts-ignore
        if (e.target.checked) {
            $makeShareGroupDefaultButton.prop('disabled', false)
            $selectElement.prop('disabled', false)
        } else {
            $makeShareGroupDefaultButton.prop('disabled', true)
            $selectElement.prop('disabled', true)
        }
    });

    $makeShareGroupDefaultButton.on('click', (e) => {
        e.stopPropagation()
        e.preventDefault()
        const vals: any = $selectElement.val();
        if (vals.length > 0) {
            localStorage.setItem('useShareGroupDefault', "true");
            localStorage.setItem('shareGroupDefaults', JSON.stringify(vals))
        } else {
            localStorage.setItem('useShareGroupDefault', "false");
        }
    })

}

loadConfigureShareGroups();

function punchcardChanged() {
    $("#phantom-select").empty();
    const phantoms = $("#punchcard-select").find("option:selected").data('phantom');
    if (phantoms)
    {
        const phantom_list = (""+phantoms).split(",");

        if (phantom_list.length > 1)
        {
            $("#phantom-select").append($("#phantom-select-shadow").find("option:disabled").clone());
        }

        for (const phantom of phantom_list)
        {
            $("#phantom-select").append($("#phantom-select-shadow").find(`option[value="${phantom}"]`).clone());
        }
    }
    else
    {
        $("#phantom-select").append($("#phantom-select-shadow").find(`option`).clone());
    }
    phantomSelectChange();
}

$("#punchcard-select").on("change", function(event: any) { punchcardChanged(); });
punchcardChanged();

export async function refreshPunchcardHeader() {
    const url = window.url_for('list_punchcards');
    const ret = await Axios.get(url);
    $('#punchcard-hole').empty();
    $('#punchcard-select').empty();
    let first = true; 
    
    $('#upload-data-btn').prop('disabled', true);

    if (ret.data.length == 0)
    {
        $("#punchcard-hole").append(`<a href="/profile#study-packages" class="m-2 badge badge-warning">No Study Packages</a>`)
    } 

    for (let p of ret.data) {
        if (p.remaining_punches != 0) {
            $('#upload-data-btn').prop('disabled', false);
        }
        $("#punchcard-hole").append(`
<span class="punchcard mt-2 mr-2" data-id="${p.id}" data-remaining-punches="${p.remaining_punches}" data-name="${p.name}" data-phantom="${p.restrictions.phantom}" data-serial-number="${p.restrictions.serial_number}" data-custom-protocols="${p.restrictions.custom_protocols}">
    <small>
      <span id="punchcard-icon-${p.id}" class="fas fa-coins punchcard-icon" style="font-size:large;"></span>
    </small>
    <small class="badge badge-danger" style="position:relative; top:-10px; left:-3px; z-index:-1">${p.remaining_punches >= 0 ? p.remaining_punches : 'Unlimited'}</small>
</span>`);
        const contents = `<strong>${p.name}</strong><br/><span class="punchcard-info-header">Scanner</span><span class="punchcard-info-data">${p.restrictions.serial_number.join(", ") || "Any"}</span><br/><span class="punchcard-info-header">Phantom</span><span class="punchcard-info-data">${p.restrictions.phantom.join(", ") || "Any"}</span><br/><span class="punchcard-info-header">Custom Protocols</span><span class="punchcard-info-data">${p.restrictions.custom_protocols.join(", ") || "None"}</span><br/<span class="punchcard-info-header">Studies Remaining</span><span class="punchcard-info-data">${p.remaining_punches < 0 ? 'Unlimited' : p.remaining_punches}</span><hr/><p class="text-center my-0 "><a href="/profile#study-packages">Details & History</a></p>`;
        ($(`#punchcard-icon-${p.id}`) as any).popover({
            content:contents,
            html:true,
            placement:'bottom', 
            trigger: 'hover',
            delay: {'show': 50, 'hide': 250},
            container: $(`#punchcard-icon-${p.id}`)
        });
        $('#punchcard-select').append(`<option ${first ? ' selected ' : ''} value="${p.id}" data-phantom="${p.restrictions.phantom.join(",")}" data-custom-protocols="${p.restrictions.custom_protocols}">${p.name}</option>`);
        first=false;
    }
    punchcardChanged();
}

$("#btn-invite-user-save").on('click', function() {
    var user_email = $('#user-email-input').val();
    $.ajax({url: '/api/invite_user',
            data: JSON.stringify({"user_email": user_email}),
            contentType: "application/json",
            type: 'POST',
            success: function() {
                const flash_template = store.getTemplate('flash-template'); 
                const flash_div = flash_template({
                    "type": "success",
                    "message": "Invitation sent to " + user_email + ". If the invitation has not arrived within a few minutes it may have been sent to the spam folder."
                });
                $("body").prepend(flash_div);
            },
            error: function(jqXHR, txtStatus, errorThrown) {
                const flash_template = store.getTemplate('flash-template'); 
                const flash_div = flash_template({
                    "type": "danger",
                    "message": "Failed to invite " + user_email + ": " + jqXHR.responseJSON.message
                });
                $("body").prepend(flash_div);
            }
        });
        $('#invite-user-modal').modal('hide');
    });

/* allow stacking modals, so we can pop an error modal over another modal */
$(document).on('show.bs.modal', '.modal', function (event) {
    var zIndex = 1050 + (10 * $('.modal:visible').length);
    $(this).css('z-index', zIndex);
    setTimeout(function() {
        $('.modal-backdrop').not('.modal-stack').css('z-index', zIndex - 1).addClass('modal-stack');
    }, 0);
});

export function openPostPage(url: string, data: any, new_window: boolean = false) {
    var form = document.createElement('form');
    document.body.appendChild(form);
    if (new_window)
        form.target = '_blank';
    form.method = 'post';
    form.action = url;
    for (var name in data) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = data[name];
        form.appendChild(input);
    }
    form.submit();
    document.body.removeChild(form);
}

export function getZipFile(url: string, args: any, retryCount: number, origBgColor?: string, thistr?: JQuery<HTMLElement>) {
    $.ajax({
        url: window.url_for(url, args),
        type: 'GET',
        success: function (data) {
            if (data.is_local) {
                console.log("Running with MINIMIZE_S3, zip was created in your $HOME/Downloads directory.");
                return
            }
            if (data.retry) {
                retryCount++;
                if (retryCount < 60) {
                    setTimeout(getZipFile, 2000, url, args, retryCount, origBgColor, thistr)
                }
                else {
                    if (origBgColor != undefined) {
                        $(thistr).animate({backgroundColor: origBgColor});
                    }
                    showErrorModal("Dicom download timed out.", "Exceeded the number of retries for downloading dicom zip file.");
                }
            }
            else if (!data.retry) {
                var zip_a = document.createElement("a");
                zip_a.href = data.zip_url;
                zip_a.click();
            }
        },
        error: function (jqXHR, txtStatus, errorThrown) {
            if (origBgColor != undefined) {
                $(thistr).animate({backgroundColor: origBgColor});
            }
            showErrorModal("Error downloading dicom zip.", "Received error: " + jqXHR.responseText);
        }
    });
}

$("#upload-data-btn").on("click", async ()=>{show_upload_modal();})


export async function show_upload_modal() {
    let modal = $('#modalUpload');
    modal.find(".new-upload").show();
    modal.find(".copy-upload").hide();
    if(copyStudyUid !== null) {
        copyStudyUid = null;
        modal.find("#copy-description").first().text("");
        modal.find("#copy-date").first().text("");
        modal.find("#serial-number").first().val("");
        modal.find("#phantom-select").first().val("").trigger("change");
        modal.find("#lc-profile-select").first().val("Use LC Thermometer").trigger("change");
        modal.find("#upload-button").text("Upload");    
    }
    modal.modal('show');
}

export async function show_copy_modal(uid:string) {
    copyStudyUid = uid;
    
    const url = window.url_for('get_upload', {"id":uid});
    const ret:any = await Axios.get(url).catch(()=>{showErrorModal("Unknown Error", "An unhandled error occured while copying the study.  Please contact CaliberMRI for help.")});
    let data = ret?.data;
    if (!data) {
        return;
    }

    let modal = $('#modalUpload');
    modal.find(".new-upload").hide();
    modal.find(".copy-upload").show();
    modal.find("#copy-description").first().text(data.description);
    modal.find("#copy-date").first().text(data.created_at);
    modal.find("#serial-number").first().val(data.serial_number);
    console.log(ret?.data.phantom)
    modal.find("#phantom-select").first().val(data.phantom_type).trigger("change");
    let temp = data.lc_thermometer;
    if(temp === null) {
        modal.find("#lc-profile-select").first().val("Use LC Thermometer").trigger("change");
        modal.find("#temperature-override").hide();
    } else {
        modal.find("#lc-profile-select").first().val("User Specified").trigger("change");
        modal.find("#temperature-override").val(temp);
    }
    modal.find("#upload-button").text("Copy Study");
    modal.modal('show');
}

export async function show_change_owner_modal(uid: string) {
    copyStudyUid = uid;
    const url = window.url_for('get_upload', {"id": uid});
    const ret: any = await Axios.get(url).catch(() => {
        showErrorModal("Unknown Error", "An unhandled error occured while copying the study.  Please contact CaliberMRI for help.")
    });
    let data = ret?.data;
    const userData: any = (await Axios.get(window.url_for('admin_list_users'), {params: {search: data.created_by}})).data.users[0]

    if (!data) {
        return;
    }
    let modal = $('#modalUpload');
    for (const cls of ['.modal-header', '.modal-body', '.modal-footer']) {
        for (const elt of modal.find(cls).contents()) {
            $(elt).hide()
        }
    }

    const currentUserEmail = data.created_by;
    let $modalContent: JQuery<HTMLElement> = modal.find("#change-owner-warning-container")

    function loadUsers($studyOwnerSelect: any, users: any) {
        $studyOwnerSelect.empty()
        for (const user of users) {
            let $option = $(`<option value="${user.id}">${user.name} - ${user.email}</option>`);
            $studyOwnerSelect.append($option)
        }
    }

    if ($modalContent.length == 0) {
        $modalContent.detach()
    }
    $modalContent = $(`
<form id="change-owner-warning-container">
    <div class="form-group">
        <h2>Change Study Owner</h2>
        <div id="change-study-owner-warning">
        <p>Are you sure you want to change the study owner? Type the owner's email ${currentUserEmail} below to
            confirm.</p>
        <input type="text" class="form-control" id="current-study-owner-name"
               placeholder="Enter owner's email: ${currentUserEmail}" required>
       </div>
    </div>
    <div class="form-group">
        <label for="study-owner-search">Search for New Owner</label>
        <input type="text" class="form-control" id="study-owner-search" placeholder="Enter username or email" autocomplete="false">
    </div>
    <div class="form-group">
        <label for="study-owner-select">Select New Owner</label>
        <select class="form-control" id="study-owner-select" required></select>
    </div>
    <button id="confirm-study-owner-change" class="btn btn-danger">Confirm Change</button>
</form>
        `)
    $(modal.find('.modal-body')).append($modalContent)
    const $studyOwnerSearch = $modalContent.find('#study-owner-search')
    const $studyOwnerSelect = $modalContent.find('#study-owner-select')
    const $changeStudyOwnerWarningContainer = $modalContent.find('change-owner-warning-container')
    const $confirmStudyOwnerChangeButton = $modalContent.find('#confirm-study-owner-change')
    const $changeStudyOwnerWarning = $modalContent.find('#change-study-owner-warning')

    let currentUserIsOwner = window.current_user_id == userData.id
    if (currentUserIsOwner) $changeStudyOwnerWarning.detach()
    // if (currentUserIsOwner) $changeStudyOwnerWarning.detach()
    $studyOwnerSearch.on('change', async (e) => {
        // @ts-ignore
        let search = e.target.value
        let result: any
        if (search === null || search.trim() === "") {
            result = await Axios.get(window.url_for('admin_list_users'))
        } else {
            result = await Axios.get(window.url_for('admin_list_users'), {params: {"search": search}})
        }
        loadUsers($studyOwnerSelect, result?.data?.users)
    })



    async function submitStudyChangeOwnershipRequest(e: any) {
        for (const $elt of $changeStudyOwnerWarningContainer.contents()) {
            // @ts-ignore
            if ($($elt)[0].checkValidity && !$($elt)[0].checkValidity()) {
                return false;
            }
        }

        if ($studyOwnerSelect.find('*:selected').val() === undefined) {
            return false;
        }

        e.preventDefault()

        const payload = {
            study_id: data.id,
            new_owner_id: $studyOwnerSelect.find('*:selected').val(),
        }
        if (!currentUserIsOwner) {
            // @ts-ignore
            payload.confirmation = $('#current-study-owner-name').val()
        }

        let update_study_url = window.url_for(
            'update_study_upload_created_by_and_cascade',
            {'study_id': payload.study_id}
        )
        try {
            await Axios.put(update_study_url, payload)
            modal.find('.close').trigger('click')
            window.location.reload()
        } catch (error) {
            const result = error.response
            const errorText = `${result.status}:${result.statusText} - ${result.data.message}`
            const errorClass = "change-study-owner-error-notification"
            const $errorNotification = $(`
<div class=${errorClass}>
${errorText}
</div>`
            )
            $modalContent.find(`.${errorClass}`).detach()
            $modalContent.append($errorNotification)
        }
    }

    $changeStudyOwnerWarningContainer.on('submit',  submitStudyChangeOwnershipRequest)
    $confirmStudyOwnerChangeButton.on('click', submitStudyChangeOwnershipRequest)


    loadUsers($modalContent.find('#study-owner-select'), (await Axios.get(window.url_for('admin_list_users'))).data?.users)
    modal.modal('show')

}


refreshPunchcardHeader()

$(document).ready(function () {
    $.ajax({
        url: "/api/current_user",
        success: function(data) {
            window.current_user_email = data.current_user.email;
        },
        error: (jqXHR) => {
        },
    });
});

// fix for toast object, see https://github.com/BoulderLabs/phantom/issues/879
$('.toast').addClass('toasty')